import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';

interface Task {
  id: string;
  title: string;
  category: string;
  active: boolean;
  mode?: string;
  createdAt: any;
}

const ModelQATaskSelector: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'tasks'),
      (snapshot) => {
        const tasksData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Task[];
        console.log('📊 [ModelQA TaskSelector] Loaded tasks:', tasksData.length, tasksData);
        setTasks(tasksData);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  // Filter tasks based on selected filters
  useEffect(() => {
    let filtered = tasks;

    // Filter by active status
    if (activeFilter === 'active') {
      filtered = filtered.filter(task => task.active === true);
    } else if (activeFilter === 'inactive') {
      filtered = filtered.filter(task => task.active === false);
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(task => task.category === categoryFilter);
    }

    setFilteredTasks(filtered);
  }, [tasks, activeFilter, categoryFilter]);

  const handleTaskSelect = (taskId: string) => {
    navigate(`/labeling/model-qa/${taskId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading tasks...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Model & QA</h1>
        <p className="text-gray-600">Select a task to view model QA metrics and readiness</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Tasks</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Active Status</label>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Tasks</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {Array.from(new Set(tasks.map(task => task.category))).map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Available Tasks</h3>
          <p className="text-sm text-gray-500">
            Choose a task to configure model QA settings ({filteredTasks.length} tasks)
          </p>
          {/* Debug info */}
          <div className="mt-2 text-xs text-gray-400">
            Total tasks in database: {tasks.length} | 
            Categories: {Array.from(new Set(tasks.map(task => task.category))).join(', ')} |
            Active: {tasks.filter(task => task.active).length} | 
            Inactive: {tasks.filter(task => !task.active).length}
            {tasks.length > 0 && (
              <div className="mt-1">
                Sample task: {JSON.stringify(tasks[0], null, 2)}
              </div>
            )}
          </div>
        </div>

        {filteredTasks.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Found</h3>
            <p className="text-gray-500">
              {tasks.length === 0 
                ? "Create a task first to configure model QA." 
                : "No tasks match the current filters. Try adjusting your filter settings."
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => handleTaskSelect(task.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{task.title}</h4>
                    <p className="text-sm text-gray-500">{task.category}</p>
                    <p className="text-xs text-gray-400">
                      Created: {task.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      task.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {task.active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/tasks`, '_blank');
                      }}
                      className="text-blue-600 hover:text-blue-800 text-xs"
                      title="Open task settings"
                    >
                      ⚙️ Settings
                    </button>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelQATaskSelector;
